import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AnalyticsLoaderService } from '../service/clarity/analytics-loader.service';
import { ClarityLoaderService } from '../service/clarity/clarity-loader.service';
import { CookieConsentService } from './cookie-consent.service';
import type { CookieConsentState } from './cookie-consent.model';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentOrchestratorService implements OnDestroy {
  private subscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;

  constructor(
    private readonly consent: CookieConsentService,
    private readonly clarityLoader: ClarityLoaderService,
    private readonly analyticsLoader: AnalyticsLoaderService,
    private readonly router: Router
  ) {
    this.subscription = this.consent.state$.subscribe((state) => {
      this.applyConsent(state);
    });

    if (!environment.consentRequired) {
      this.applyDevBypass();
    }

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (!this.consent.isProviderAccepted('googleAnalytics')) {
          return;
        }
        if (!this.analyticsLoader.isLoaded()) {
          return;
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'page',
          pageName: event.urlAfterRedirects,
        });
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  private applyDevBypass(): void {
    if (environment.clarityEnabled) {
      this.clarityLoader.enable(environment.clarityProjectId);
    }
    this.analyticsLoader.enable();
  }

  private applyConsent(state: CookieConsentState | null): void {
    if (!environment.consentRequired) {
      return;
    }

    if (!state) {
      this.clarityLoader.disable();
      this.analyticsLoader.disable();
      return;
    }

    if (state.clarity === 'accepted' && environment.clarityEnabled) {
      this.clarityLoader.enable(environment.clarityProjectId);
    } else {
      this.clarityLoader.disable();
      if (state.clarity === 'rejected') {
        this.consent.clearFirstPartyOptionalCookies();
      }
    }

    if (state.googleAnalytics === 'accepted') {
      this.analyticsLoader.enable();
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'page',
        pageName: this.router.url,
      });
    } else {
      this.analyticsLoader.disable();
      if (state.googleAnalytics === 'rejected') {
        this.consent.clearGoogleAnalyticsCookies();
      }
    }
  }
}
